import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { MapPin, Clock, CreditCard, Truck, CheckCircle, ChefHat, Package } from 'lucide-react';
import api from '../../services/api';
import Logo from '../../components/Logo';
import io from 'socket.io-client';
import './AcompanharPedido.css';

const AcompanharPedido = () => {
  const { numero } = useParams();
  const [pedido, setPedido] = useState(null);
  const [socket, setSocket] = useState(null);

  // Buscar pedido inicial
  const { data: pedidoData, refetch } = useQuery(
    ['pedido-numero', numero],
    async () => {
      const response = await api.get(`/api/pedidos/acompanhar/${numero}`);
      return response.data.data.pedido;
    },
    {
      onSuccess: (data) => {
        setPedido(data);
      },
      refetchInterval: 5000, // Refetch a cada 5 segundos como fallback
      enabled: !!numero
    }
  );

  // Conectar Socket.io para atualizações em tempo real
  useEffect(() => {
    if (!pedido?._id) return;

    // Usar mesma lógica do api.js para determinar URL
    let socketURL;
    if (process.env.NODE_ENV === 'production') {
      // Em produção, usar window.location.origin (mesmo domínio)
      // O Apache fará proxy de /socket.io para localhost:5000
      socketURL = typeof window !== 'undefined' ? window.location.origin : '';
    } else {
      // Em desenvolvimento, usar variável de ambiente ou localhost
      socketURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    }

    const socketConnection = io(socketURL, {
      transports: ['websocket', 'polling'],
      path: '/socket.io/'
    });
    
    socketConnection.on('connect', () => {
      console.log('✅ Conectado ao servidor Socket.io');
      socketConnection.emit('subscribe-pedido', pedido._id);
    });

    socketConnection.on('pedido-atualizado', (pedidoAtualizado) => {
      console.log('📦 Pedido atualizado via Socket.io:', pedidoAtualizado);
      setPedido(pedidoAtualizado);
      refetch(); // Atualizar também via API
    });

    socketConnection.on('pedido-status-changed', (data) => {
      if (data.numero === numero) {
        refetch(); // Buscar atualização completa
      }
    });

    socketConnection.on('disconnect', () => {
      console.log('❌ Desconectado do servidor Socket.io');
    });

    setSocket(socketConnection);

    return () => {
      socketConnection.emit('unsubscribe-pedido', pedido._id);
      socketConnection.disconnect();
    };
  }, [pedido?._id, numero, refetch]);

  if (!pedido && !pedidoData) {
    return (
      <div className="acompanhar-loading">
        <div className="loading-spinner"></div>
        <p>Carregando pedido...</p>
      </div>
    );
  }

  const pedidoAtual = pedido || pedidoData;

  const statusSteps = [
    { status: 'Pendente', label: 'Aguardando', icon: Clock, color: '#9CA3AF' },
    { status: 'Confirmado', label: 'Confirmado', icon: CheckCircle, color: '#3B82F6' },
    { status: 'Em preparo', label: 'Preparando', icon: ChefHat, color: '#F59E0B' },
    { status: 'Pronto', label: 'Pronto', icon: Package, color: '#10B981' },
    { status: 'Saiu para entrega', label: 'Saiu para Entrega', icon: Truck, color: '#8B5CF6' },
    { status: 'Chegou no endereço', label: 'Chegou no Endereço', icon: MapPin, color: '#EF4444' },
    { status: 'Entregue', label: 'Entregue', icon: CheckCircle, color: '#10B981' }
  ];

  const statusIndex = statusSteps.findIndex(s => s.status === pedidoAtual.status);
  const statusAtual = statusSteps[statusIndex] || statusSteps[0];

  const formatarPagamento = (forma) => {
    const formas = {
      'dinheiro': 'Dinheiro',
      'pix': 'PIX',
      'cartao_credito': 'Cartão de Crédito',
      'cartao_debito': 'Cartão de Débito',
      'outro': 'Outro'
    };
    return formas[forma] || forma;
  };

  return (
    <div className="acompanhar-pedido">
      <div className="acompanhar-container">
        {/* Header */}
        <div className="acompanhar-header">
          <Logo size="medium" />
          <div className="pedido-numero-header">
            <h1>Pedido #{pedidoAtual.numero}</h1>
            <p className="cliente-nome">Olá, <strong>{pedidoAtual.cliente.nome}</strong>!</p>
          </div>
        </div>

        {/* Timeline de Status */}
        <div className="status-timeline">
          {statusSteps.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = index <= statusIndex;
            const isCurrent = index === statusIndex;
            
            return (
              <div 
                key={step.status} 
                className={`timeline-step ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`}
              >
                <div className="step-icon-wrapper">
                  <div className="step-icon" style={{ backgroundColor: isActive ? step.color : '#E5E7EB' }}>
                    <StepIcon size={20} color="white" />
                  </div>
                  {index < statusSteps.length - 1 && (
                    <div className={`step-line ${isActive ? 'active' : ''}`}></div>
                  )}
                </div>
                <div className="step-label">{step.label}</div>
              </div>
            );
          })}
        </div>

        {/* Detalhes do Pedido */}
        <div className="pedido-detalhes-grid">
          {/* Produtos */}
          <div className="detalhe-card">
            <h3>📦 Produtos</h3>
            <div className="produtos-list">
              {pedidoAtual.itens.map((item, index) => (
                <div key={index} className="produto-item">
                  <div className="produto-info">
                    <span className="produto-quantidade">{item.quantidade}x</span>
                    <div className="produto-detalhes">
                      <span className="produto-nome">{item.produto?.nome || 'Produto'}</span>
                      {item.observacoes && (
                        <small className="produto-obs">{item.observacoes}</small>
                      )}
                    </div>
                  </div>
                  <span className="produto-preco">
                    R$ {(item.precoUnitario * item.quantidade).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Informações */}
          <div className="detalhe-card">
            <h3>ℹ️ Informações</h3>
            <div className="info-list">
              <div className="info-item">
                <Clock size={20} />
                <div>
                  <strong>Tempo Estimado:</strong>
                  <p>{pedidoAtual.tempoEstimado} minutos</p>
                </div>
              </div>
              
              <div className="info-item">
                <CreditCard size={20} />
                <div>
                  <strong>Forma de Pagamento:</strong>
                  <p>{formatarPagamento(pedidoAtual.formaPagamento)}</p>
                </div>
              </div>

              {pedidoAtual.tipoEntrega === 'delivery' && pedidoAtual.enderecoEntrega && (
                <div className="info-item">
                  <MapPin size={20} />
                  <div>
                    <strong>Endereço de Entrega:</strong>
                    <p>
                      {pedidoAtual.enderecoEntrega.rua}, {pedidoAtual.enderecoEntrega.numero}
                      {pedidoAtual.enderecoEntrega.complemento && ` - ${pedidoAtual.enderecoEntrega.complemento}`}
                    </p>
                    <p>
                      {pedidoAtual.enderecoEntrega.bairro} - {pedidoAtual.enderecoEntrega.cidade}/{pedidoAtual.enderecoEntrega.estado}
                    </p>
                    {pedidoAtual.enderecoEntrega.referencia && (
                      <p className="referencia">Ref: {pedidoAtual.enderecoEntrega.referencia}</p>
                    )}
                  </div>
                </div>
              )}

              {pedidoAtual.tipoEntrega === 'retirada' && (
                <div className="info-item">
                  <MapPin size={20} />
                  <div>
                    <strong>Retirada no Local</strong>
                    <p>Venha buscar seu pedido no estabelecimento</p>
                  </div>
                </div>
              )}

              {/* Mostrar entregador quando status for Pronto, Saiu para entrega ou Entregue */}
              {pedidoAtual.entregador && 
               (pedidoAtual.status === 'Pronto' || 
                pedidoAtual.status === 'Saiu para entrega' || 
                pedidoAtual.status === 'Chegou no endereço' ||
                pedidoAtual.status === 'Entregue') && (
                <div className="info-item entregador-info">
                  <Truck size={20} />
                  <div>
                    <strong>🚴 Entregador:</strong>
                    <p className="entregador-nome">{pedidoAtual.entregador.nome || 'Aguardando atribuição'}</p>
                    {pedidoAtual.entregador.telefone && (
                      <p className="telefone-entregador">📱 {pedidoAtual.entregador.telefone}</p>
                    )}
                    {pedidoAtual.status === 'Saiu para entrega' && (
                      <p className="status-entrega">🚚 A caminho da sua casa!</p>
                    )}
                    {pedidoAtual.status === 'Chegou no endereço' && (
                      <p className="status-entrega" style={{ color: '#EF4444', fontWeight: 'bold' }}>
                        🚪 Entregador chegou no seu endereço! Por favor, esteja disponível!
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Resumo */}
          <div className="detalhe-card resumo-card">
            <h3>💰 Resumo do Pedido</h3>
            <div className="resumo-list">
              <div className="resumo-item">
                <span>Subtotal dos Produtos:</span>
                <span>R$ {pedidoAtual.subtotal?.toFixed(2) || '0.00'}</span>
              </div>
              {pedidoAtual.tipoEntrega === 'delivery' && (
                <div className="resumo-item" style={{ 
                  background: (pedidoAtual.taxaEntrega || 0) > 0 ? '#fef3c7' : '#d1fae5',
                  padding: '0.5rem',
                  borderRadius: '0.25rem',
                  marginTop: '0.5rem'
                }}>
                  <span style={{ fontWeight: '600' }}>Taxa de Entrega:</span>
                  <span style={{ 
                    fontWeight: '700', 
                    color: (pedidoAtual.taxaEntrega || 0) > 0 ? '#92400e' : '#059669' 
                  }}>
                    {pedidoAtual.taxaEntrega > 0 
                      ? `R$ ${pedidoAtual.taxaEntrega.toFixed(2)}` 
                      : 'Grátis'}
                  </span>
                </div>
              )}
              <div className="resumo-total" style={{ 
                marginTop: '1rem', 
                paddingTop: '1rem', 
                borderTop: '2px solid #e5e7eb' 
              }}>
                <span style={{ fontSize: '1.1rem', fontWeight: '600' }}>Total a Pagar:</span>
                <strong style={{ fontSize: '1.3rem', color: '#059669' }}>
                  R$ {pedidoAtual.total?.toFixed(2) || (pedidoAtual.subtotal || 0).toFixed(2)}
                </strong>
              </div>
            </div>
          </div>
        </div>

        {/* Status Atual */}
        <div className="status-atual-card">
          <div className="status-atual-content">
            <statusAtual.icon size={24} color={statusAtual.color} />
            <div>
              <p className="status-atual-label">Status Atual</p>
              <p className="status-atual-value" style={{ color: statusAtual.color }}>
                {statusAtual.label}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcompanharPedido;

